import { useLazyQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { COMPANY_MATCH_FILE_UPLOAD } from "../../../apis/companyMatch/queries";
import { CompanyMatchUploadPayload } from "../../../types/companyMatch";
import { COMPANY_MATCH_CLIENT } from "../../../utils/consts";
import { FileInput } from "../../input/FileInput/FileInput";
import { FILE_SETTINGS } from "./constants";
import { ErrorState } from "./ErrorState";
import { IdleState } from "./IdleState";
import { InfoAlert } from "./InfoAlert";
import { ProcessingState } from "./ProcessingState";
import { SuccessState } from "./SuccessState";
import {
  CustomerUploadSectionProps,
  FileUploadFilterMode,
  UPLOAD_STATE,
  UploadState,
} from "./types";

interface Props extends CustomerUploadSectionProps {
  showAlert?: boolean;
}

export const FileUploadFilter: React.FC<Props> = ({
  matchData,
  onUpdateMatchData,
  mode = FileUploadFilterMode.DEFINE_WHITESPACE_FILTER,
  id = "file-upload-filter",
  showAlert = true,
}) => {
  const [uploadState, setUploadState] = useState<UploadState>(
    UPLOAD_STATE.IDLE
  );
  const [errors, setErrors] = useState<string[]>([]);

  // Determine initial upload state based on matchData
  useEffect(() => {
    if (!matchData?.fileId) {
      setUploadState(UPLOAD_STATE.IDLE);
      return;
    }

    if (matchData?.lastMatched) {
      setUploadState(UPLOAD_STATE.SUCCESS);
    } else {
      setUploadState(UPLOAD_STATE.PROCESSING);
    }
  }, [matchData]);

  const [companyMatchUploadQuery, { error: uploadError }] = useLazyQuery<
    CompanyMatchUploadPayload,
    {
      file: File;
      isPlatformPermissible: boolean;
      isV1Dataset: boolean;
      rgifExclusiveCompanies: boolean;
    }
  >(COMPANY_MATCH_FILE_UPLOAD, {
    context: { clientName: COMPANY_MATCH_CLIENT },
    fetchPolicy: "no-cache",
    onCompleted: (data: CompanyMatchUploadPayload) => {
      onUpdateMatchData(data.upload);
      setUploadState(UPLOAD_STATE.PROCESSING);
    },
  });

  // Handle upload errors from the query
  useEffect(() => {
    if (uploadError) {
      setErrors([uploadError.message]);
      setUploadState(UPLOAD_STATE.ERROR);
    }
  }, [uploadError]);

  const handleReset = useCallback(() => {
    setUploadState(UPLOAD_STATE.IDLE);
    setErrors([]);
  }, []);

  const handleFilesAccepted = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return;

      setUploadState(UPLOAD_STATE.UPLOADING);
      companyMatchUploadQuery({
        variables: {
          file: files[0],
          isPlatformPermissible: true,
          isV1Dataset: false,
          rgifExclusiveCompanies: true,
        },
      });
    },
    [companyMatchUploadQuery]
  );

  const uploadFileStateContent = useMemo(() => {
    return (
      <FileInput
        id="customer-list-upload"
        acceptedFileTypes={FILE_SETTINGS.ACCEPTED_TYPES}
        acceptedFileExtensions={FILE_SETTINGS.ACCEPTED_EXTENSIONS}
        // Intentionally disabled: whitespace CSV files can be larger than 5MB.
        // We validate file constraints server-side to avoid blocking valid uploads in the UI.
        // maxSize={MAX_FILE_SIZE}
        maxFiles={1}
        onFilesAccepted={handleFilesAccepted}
        customContent={
          <IdleState isUploading={uploadState === UPLOAD_STATE.UPLOADING} />
        }
      />
    );
  }, [uploadState, handleFilesAccepted]);

  const renderContentByState = useMemo(() => {
    switch (uploadState) {
      case UPLOAD_STATE.PROCESSING:
        return <ProcessingState uploadResult={matchData} mode={mode} />;
      case UPLOAD_STATE.SUCCESS:
        return (
          matchData && <SuccessState uploadResult={matchData} mode={mode} />
        );
      case UPLOAD_STATE.ERROR:
        return <ErrorState errors={errors} onReset={handleReset} />;
      default:
        return uploadFileStateContent;
    }
  }, [
    uploadState,
    uploadFileStateContent,
    matchData,
    handleReset,
    errors,
    mode,
  ]);

  return (
    <Box id={id}>
      {showAlert && (
        <InfoAlert
          hideAlert={
            uploadState === UPLOAD_STATE.SUCCESS ||
            uploadState === UPLOAD_STATE.PROCESSING ||
            mode === FileUploadFilterMode.ICP_CUSTOMER_LIST
          }
          mode={mode}
        />
      )}
      <Fade in={true} timeout={500}>
        <Box id={`${id}-content`}>{renderContentByState}</Box>
      </Fade>
    </Box>
  );
};
